// All site copy, two locales. Japanese (ja) is primary; English (en) mirrors it.
// Tane (種) — an AI learning companion. Content is product-focused:
// product → solutions → use cases, grounded in real market research (sources
// cited in the Market section).

export interface Step {
  k: string; // seed-stage kanji: 種 / 芽 / 実
  t: string;
  d: string;
}
export interface Feature {
  n: string;
  t: string;
  d: string;
  bullets: string[];
}
export interface Solution {
  problem: string;
  problemDetail: string;
  stat: string;
  solution: string;
}
export interface UseCase {
  tag: string;
  t: string;
  d: string;
}
export interface Stat {
  value: string;
  label: string;
  source: string;
}
export interface Tier {
  name: string;
  kana: string;
  price: string;
  unit: string;
  tagline: string;
  features: string[];
  cta: string;
  featured: boolean;
}
export interface QA {
  q: string;
  a: string;
}

export interface Content {
  nav: { how: string; features: string; demo: string; pricing: string; faq: string; cta: string };
  langSwitch: string;
  hero: {
    chip: string;
    kicker: string;
    titleTop: string;
    titleEm: string;
    titleBottom: string;
    lead: string;
    ctaPrimary: string;
    ctaSecondary: string;
    miniStats: { value: string; label: string }[];
  };
  subjects: { label: string; items: string[] };
  how: { label: string; title: string; lead: string; steps: Step[] };
  features: { label: string; title: string; lead: string; items: Feature[] };
  demo: {
    label: string;
    title: string;
    lead: string;
    subjects: { id: string; label: string }[];
    difficulty: string;
    diffEasy: string;
    diffHard: string;
    mastery: string;
    check: string;
    next: string;
    correct: string;
    incorrect: string;
    hintLabel: string;
    showHint: string;
    explainLabel: string;
    restart: string;
    done: string;
    doneNote: string;
    chatTitle: string;
    chatPlaceholder: string;
    chatTyping: string;
    chatHint: string;
  };
  solutions: {
    label: string;
    title: string;
    lead: string;
    items: Solution[];
    problemLabel: string;
    solutionLabel: string;
    diagram: { title: string; caption: string; conventional: string; withTane: string; axis: string };
  };
  usecases: { label: string; title: string; lead: string; items: UseCase[]; imageAlt: string };
  market: {
    label: string;
    title: string;
    lead: string;
    stats: Stat[];
    note: string;
    chart: { title: string; caption: string; cagr: string; unit: string; years: string[] };
    place: { title: string; caption: string; alt: string };
  };
  pricing: { label: string; title: string; lead: string; billingNote: string; perMonth: string; tiers: Tier[] };
  faq: { label: string; title: string; items: QA[] };
  cta: { label: string; title: string; lead: string; emailLabel: string; email: string; button: string; placeholder: string; note: string };
  footer: { tagline: string; status: string; location: string; rights: string };
}

const ja: Content = {
  nav: { how: "しくみ", features: "機能", demo: "体験", pricing: "料金", faq: "Q&A", cta: "無料ではじめる" },
  langSwitch: "EN",
  hero: {
    chip: "AI学習コンパニオン",
    kicker: "Tane（たね）／ 名古屋",
    titleTop: "学びの種を、",
    titleEm: "一人ひとり",
    titleBottom: "に。",
    lead: "Tane は、AIがあなたのレベル・目標・ペースに合わせて学びを育てる学習コンパニオンです。診断から個別カリキュラム、リアルタイムの難易度調整、24時間対応のAIチューターまで。塾の約10分の1の月額で、一人ひとりに「専属の家庭教師」を。",
    ctaPrimary: "無料で試す",
    ctaSecondary: "デモを体験する",
    miniStats: [
      { value: "月¥1,980〜", label: "塾の約1/10の費用" },
      { value: "24時間", label: "いつでもAIチューター" },
      { value: "全科目", label: "目標に合わせて対応" },
    ],
  },
  subjects: {
    label: "対応する学び",
    items: ["英語", "数学", "国語", "理科", "プログラミング", "日本語", "TOEIC・資格", "リスキリング"],
  },
  how: {
    label: "しくみ",
    title: "種をまき、育て、実らせる。",
    lead: "Tane は、植物を育てるように学びを伸ばします。たった3ステップ。",
    steps: [
      { k: "種", t: "1. まく — 診断する", d: "数分の診断であなたの「現在地」を把握。目標から逆算して、あなただけの学習プランを設計します。" },
      { k: "芽", t: "2. 育てる — 学ぶ", d: "AIが正答・誤答からリアルタイムに難易度を調整。つまずいたら、AIチューターが対話でその場で解決します。" },
      { k: "実", t: "3. 実らせる — 定着させる", d: "習得度は「学びの庭」で可視化。最適なタイミングで復習を出題し、忘れないところまで伴走します。" },
    ],
  },
  features: {
    label: "機能",
    title: "Tane にできること",
    lead: "「一人ひとりに最適な学び」を実現する、4つのコア機能。",
    items: [
      {
        n: "01",
        t: "適応型学習エンジン",
        d: "解答の正誤・速度・つまずきから理解度を推定し、出題する問題と難易度を一問ごとに調整します。",
        bullets: ["一問ごとのリアルタイム難易度調整", "弱点を自動で特定し、優先的に出題", "「やさしすぎ／難しすぎ」をなくす"],
      },
      {
        n: "02",
        t: "AIチューター（24時間）",
        d: "答えではなくヒントを段階的に提示。納得できるまで何度でも、対話で教えてくれる専属チューター。",
        bullets: ["わからない箇所をその場で質問", "段階的ヒントで「自分で解ける」を支援", "最新のAIモデルを活用"],
      },
      {
        n: "03",
        t: "学びの庭（進捗の可視化）",
        d: "学んだ分だけ庭が育ちます。習得度・連続記録・弱点を一目で把握し、最適な復習を自動で提案。",
        bullets: ["科目ごとの習得度マップ", "間隔反復で「忘れない」学習", "保護者・先生向けレポート"],
      },
      {
        n: "04",
        t: "どんな目標にも",
        d: "受験から英語、プログラミング、資格、日本語学習まで。あなたのゴールに合わせて学びを設計します。",
        bullets: ["科目・目標を自由に設定", "スキマ時間でも続けられる設計", "日本語コンテンツに最適化"],
      },
    ],
  },
  demo: {
    label: "体験",
    title: "実際に、育ててみる。",
    lead: "これは Tane の適応型エンジンのミニ体験です。正解すると難易度が上がり、間違えるとヒントが出て易しくなります。あなたに合わせて、種が育ちます。",
    subjects: [
      { id: "english", label: "英語" },
      { id: "math", label: "数学" },
      { id: "programming", label: "プログラミング" },
    ],
    difficulty: "難易度",
    diffEasy: "やさしい",
    diffHard: "むずかしい",
    mastery: "習得度",
    check: "答え合わせ",
    next: "次の問題",
    correct: "正解！",
    incorrect: "おしい！",
    hintLabel: "ヒント",
    showHint: "ヒントを見る",
    explainLabel: "解説",
    restart: "もう一度",
    done: "よく育ちました！",
    doneNote: "Tane があなたの解答に合わせて難易度を調整しました。実際のアプリでは、全科目でこの体験が続きます。",
    chatTitle: "AIチューターに聞いてみる",
    chatPlaceholder: "質問を入力…（例：なぜ？）",
    chatTyping: "Tane が入力中…",
    chatHint: "下の質問をタップしてみてください",
  },
  solutions: {
    label: "解決すること",
    title: "「学び」の不平等を、なくす。",
    lead: "日本の教育がいま抱える課題に、Tane は一人ひとりへの個別最適化で応えます。",
    problemLabel: "課題",
    solutionLabel: "Tane の解決",
    items: [
      {
        problem: "一斉授業の限界",
        problemDetail: "30人に1人の先生では、一人ひとりの理解度に合わせきれません。",
        stat: "1対1指導は成績を約2標準偏差引き上げる（Bloom の2シグマ問題）",
        solution: "全員に「専属のAI家庭教師」を。理解度に合わせた個別指導を、誰もが受けられます。",
      },
      {
        problem: "高額な塾の費用",
        problemDetail: "塾にかかる費用は年間およそ35〜38万円（公立中高）。家計の大きな負担です。",
        stat: "塾の年間費用 約¥350,000〜380,000／人（文科省 2023）",
        solution: "月額¥1,980〜。塾の約10分の1以下で、より深い個別最適化を実現します。",
      },
      {
        problem: "深刻な教員不足",
        problemDetail: "先生が足りず、個別の手厚い指導まで手が回らないのが現実です。",
        stat: "小学校の約64%・中学校の約56%が教員不足を報告（2023年調査）",
        solution: "先生の負担を減らす個別指導アシスタントとして、学校・塾の現場を支えます。",
      },
      {
        problem: "学びが続かない",
        problemDetail: "一人だと、つまずきや単調さでモチベーションが続きません。",
        stat: "日本の英語力は116か国中92位＝過去最低（EF EPI 2024）",
        solution: "対話するAIチューターと、成長が見える「庭」。続けたくなる仕組みで、結果につなげます。",
      },
    ],
    diagram: {
      title: "なぜ「個別」なのか — 2シグマの法則",
      caption: "教育学者ベンジャミン・ブルームの研究では、1対1の個別指導を受けた生徒は、一斉授業の生徒のおよそ98%を上回りました（約2標準偏差の差）。Tane は、その「専属の家庭教師」を誰もが持てるようにします。",
      conventional: "一斉授業",
      withTane: "個別指導（Tane）",
      axis: "成績の分布",
    },
  },
  usecases: {
    label: "使い方",
    title: "こんな人に、こんな場面で。",
    lead: "Tane は、年齢も目標も問いません。あなたの「学びたい」に合わせて育ちます。",
    imageAlt: "利用シーンのイメージ",
    items: [
      { tag: "受験生", t: "弱点をねらって、効率的に。", d: "苦手だけを集中的に。志望校から逆算したプランで、限られた時間を最大化します。" },
      { tag: "社会人", t: "スキマ時間で、リスキリング。", d: "英語・プログラミング・資格を、通勤中や寝る前の数分から。続けられる設計です。" },
      { tag: "保護者", t: "塾の代わりに、家庭で。", d: "送り迎えも高額な月謝も不要。お子さまの理解度に合わせ、家庭で個別指導を。" },
      { tag: "日本語学習者", t: "生活と仕事のための日本語を。", d: "在日外国人の方へ。レベルと目的に合わせて、実用的な日本語を一歩ずつ。" },
      { tag: "学校・塾", t: "先生のための、個別指導アシスタント。", d: "クラス全体と個人の進捗を可視化。先生は一人ひとりに、より深く向き合えます。" },
      { tag: "学び直したい大人", t: "もう一度、基礎から。", d: "中学・高校の学び直しも、恥ずかしくない。あなたのペースで、誰にも見られず。" },
    ],
  },
  market: {
    label: "なぜ、いま名古屋で",
    title: "市場は、いま動いている。",
    lead: "AIと教育が交わる転換点。成長する市場と、確かな課題と、名古屋という最適な拠点が揃っています。",
    stats: [
      { value: "36.7%", label: "日本のAI教育市場の年成長率（〜2030、APAC最速）", source: "Grand View Research" },
      { value: "¥約37万", label: "子ども1人あたりの年間・塾費用（公立中）", source: "文部科学省 2023" },
      { value: "92位", label: "日本の英語力ランキング（116か国中・過去最低）", source: "EF EPI 2024" },
      { value: "約64%", label: "教員不足を報告した小学校の割合（2023）", source: "東アジアフォーラム" },
      { value: "+2σ", label: "1対1指導による成績向上の目安", source: "Bloom's 2-sigma" },
      { value: "500社+", label: "STATION Ai 開設時の入居スタートアップ（国内最大）", source: "JETRO 2024" },
    ],
    note: "出典：Grand View Research / 文部科学省 / EF EPI 2024 / East Asia Forum / JETRO ほか。市場規模は調査機関により定義が異なるため、幅をもって示しています。",
    chart: {
      title: "日本のAI教育市場（推計）",
      caption: "アジア太平洋で最速級の成長。年平均成長率 約36.7% で、2030年に向けて拡大が見込まれます。",
      cagr: "CAGR 36.7%",
      unit: "市場規模（指数・2024=100）",
      years: ["2024", "2025", "2026", "2027", "2028", "2029", "2030"],
    },
    place: {
      title: "名古屋・愛知という拠点",
      caption: "2024年10月開業の STATION Ai は、500社超が集う国内最大級のスタートアップ拠点。愛知は人口約746万人、中部圏は日本のGDPの約11%を占め、外国人住民も過去最多。教育課題と事業機会の交差点です。",
      alt: "名古屋・STATION Ai のイメージ",
    },
  },
  pricing: {
    label: "料金",
    title: "誰もが手の届く価格で。",
    lead: "まずは無料で。必要になったら、塾よりずっと手軽な月額で。いつでも変更・解約できます。",
    billingNote: "価格は税込。年額プランなら2か月分お得になります。",
    perMonth: "／月",
    tiers: [
      {
        name: "Seed", kana: "たね", price: "¥0", unit: "", tagline: "まずは試す",
        features: ["1科目の適応型学習", "AIチューター（1日5回まで）", "学びの庭（基本）", "クレジットカード不要"],
        cta: "無料ではじめる", featured: false,
      },
      {
        name: "Sprout", kana: "め", price: "¥1,980", unit: "／月", tagline: "いちばん人気",
        features: ["全科目・無制限の適応型学習", "AIチューター無制限", "学びの庭（フル）＋間隔反復", "弱点レポート", "塾の約1/10の費用"],
        cta: "Sprout をはじめる", featured: true,
      },
      {
        name: "Harvest", kana: "みのり", price: "¥4,980", unit: "／月", tagline: "本気の目標に",
        features: ["Sprout のすべて", "受験・資格の特化プラン", "優先AI（高精度モデル）", "保護者向けレポート", "月1回の学習プラン見直し"],
        cta: "Harvest をはじめる", featured: false,
      },
      {
        name: "Grove", kana: "学校・塾向け", price: "個別見積", unit: "", tagline: "学校・塾・法人",
        features: ["先生向けダッシュボード", "クラス・課題管理", "全体／個人の分析", "SSO・一括管理", "導入サポート"],
        cta: "お問い合わせ", featured: false,
      },
    ],
  },
  faq: {
    label: "Q&A",
    title: "よくあるご質問",
    items: [
      { q: "対応している科目は？", a: "英語・数学・国語・理科・プログラミング・日本語・各種資格（TOEIC等）など、幅広く対応します。目標を設定すれば、それに合わせて学習プランを設計します。" },
      { q: "AIチューターは本当に「教えて」くれますか？", a: "はい。答えをそのまま渡すのではなく、段階的なヒントと対話で、あなた自身が解けるように導きます。最新のAIモデルを用いています。" },
      { q: "塾と比べて何が違うの？", a: "塾の集団授業では難しい「一人ひとりへの完全な個別最適化」を、塾の約10分の1以下の費用で提供します。場所や時間にも縛られません。" },
      { q: "子どもに使わせても安全ですか？", a: "学習データは保護を最優先に扱い、保護者向けのレポートで学習状況を確認できます。年齢に応じた設計を進めています。" },
      { q: "無料で使えますか？", a: "はい。クレジットカード不要で、無料の Seed プランから始められます。必要になったらいつでもアップグレードできます。" },
      { q: "学校・塾で導入できますか？", a: "可能です。先生向けのダッシュボードや一括管理を備えた Grove プランをご用意しています。お気軽にお問い合わせください。" },
    ],
  },
  cta: {
    label: "はじめる",
    title: "最初の種を、まこう。",
    lead: "Tane は現在、アーリーアクセスを準備中です。ご登録いただくと、公開時にいち早くご案内します。学校・塾の方、協業・投資のご相談も歓迎します。",
    emailLabel: "Email",
    email: "mauryskull@gmail.com",
    button: "アーリーアクセスに登録",
    placeholder: "メールアドレス",
    note: "名古屋・愛知発。一人ひとりの学びを、テクノロジーで。",
  },
  footer: {
    tagline: "学びの種を、一人ひとりに。",
    status: "アーリーアクセス準備中",
    location: "名古屋・愛知、日本",
    rights: "© 2026 Tane. All rights reserved.",
  },
};

const en: Content = {
  nav: { how: "How it works", features: "Features", demo: "Try it", pricing: "Pricing", faq: "FAQ", cta: "Start free" },
  langSwitch: "日本語",
  hero: {
    chip: "AI learning companion",
    kicker: "Tane / Nagoya, Japan",
    titleTop: "A seed of learning,",
    titleEm: "for everyone",
    titleBottom: ".",
    lead: "Tane is an AI learning companion that grows with you — adapting to your level, goals and pace. From diagnosis to a personalized path, real-time difficulty tuning, and a 24/7 AI tutor. A personal tutor for each learner, at roughly one-tenth the cost of cram school.",
    ctaPrimary: "Start free",
    ctaSecondary: "Try the demo",
    miniStats: [
      { value: "¥1,980/mo", label: "~1/10 the cost of juku" },
      { value: "24/7", label: "always-on AI tutor" },
      { value: "Any subject", label: "tailored to your goal" },
    ],
  },
  subjects: {
    label: "What you can learn",
    items: ["English", "Math", "Japanese", "Science", "Programming", "Reading", "TOEIC & certs", "Reskilling"],
  },
  how: {
    label: "How it works",
    title: "Plant it. Tend it. Watch it grow.",
    lead: "Tane grows learning the way you grow a plant — in three simple steps.",
    steps: [
      { k: "種", t: "1. Plant — diagnose", d: "A few-minute diagnosis pinpoints where you are, then designs a learning plan built backwards from your goal." },
      { k: "芽", t: "2. Tend — learn", d: "The AI tunes difficulty in real time from your answers; when you get stuck, the AI tutor resolves it through dialogue, on the spot." },
      { k: "実", t: "3. Harvest — retain", d: "Mastery shows up in your “learning garden.” Tane schedules reviews at the right moment, until it truly sticks." },
    ],
  },
  features: {
    label: "Features",
    title: "What Tane does",
    lead: "Four core capabilities that make truly personalized learning real.",
    items: [
      {
        n: "01",
        t: "Adaptive learning engine",
        d: "Estimates understanding from your accuracy, speed and stumbles — then tunes the next question and its difficulty.",
        bullets: ["Real-time difficulty, question by question", "Automatically finds and targets weak spots", "No more “too easy” or “too hard”"],
      },
      {
        n: "02",
        t: "AI tutor, 24/7",
        d: "Graduated hints, not answers. A personal tutor that explains through dialogue, as many times as it takes.",
        bullets: ["Ask about anything, in the moment", "Step-by-step hints so you solve it yourself", "Powered by modern AI models"],
      },
      {
        n: "03",
        t: "Your learning garden",
        d: "The more you learn, the more it grows. See mastery, streaks and weak spots at a glance, with reviews suggested automatically.",
        bullets: ["Mastery map by subject", "Spaced repetition so it sticks", "Reports for parents and teachers"],
      },
      {
        n: "04",
        t: "Any goal you have",
        d: "From exam prep to English, programming, certifications and Japanese — learning designed around your goal.",
        bullets: ["Set any subject or goal", "Built for a few minutes at a time", "Optimized for Japanese content"],
      },
    ],
  },
  demo: {
    label: "Try it",
    title: "Grow one yourself.",
    lead: "A mini taste of Tane's adaptive engine. Get it right and difficulty rises; get it wrong and a hint appears as it eases off. The seed grows to fit you.",
    subjects: [
      { id: "english", label: "English" },
      { id: "math", label: "Math" },
      { id: "programming", label: "Programming" },
    ],
    difficulty: "Difficulty",
    diffEasy: "easy",
    diffHard: "hard",
    mastery: "Mastery",
    check: "Check",
    next: "Next question",
    correct: "Correct!",
    incorrect: "Not quite!",
    hintLabel: "Hint",
    showHint: "Show a hint",
    explainLabel: "Explanation",
    restart: "Start over",
    done: "Nicely grown!",
    doneNote: "Tane adjusted difficulty to match your answers. In the real app, this continues across every subject.",
    chatTitle: "Ask the AI tutor",
    chatPlaceholder: "Type a question… (e.g. why?)",
    chatTyping: "Tane is typing…",
    chatHint: "Tap a question below to try it",
  },
  solutions: {
    label: "What we solve",
    title: "Ending the learning gap.",
    lead: "Tane answers Japan's pressing education challenges with personalization for every learner.",
    problemLabel: "Problem",
    solutionLabel: "Tane's answer",
    items: [
      {
        problem: "One-size-fits-all classrooms",
        problemDetail: "One teacher to thirty students can't meet every learner where they are.",
        stat: "1-on-1 tutoring lifts results by ~2 standard deviations (Bloom's 2-sigma)",
        solution: "A personal AI tutor for everyone — individualized instruction anyone can access.",
      },
      {
        problem: "The high cost of juku",
        problemDetail: "Cram school runs ~¥350,000–380,000 a year (public middle/high) — a heavy burden.",
        stat: "Juku ≈ ¥350,000–380,000 / year per child (MEXT 2023)",
        solution: "From ¥1,980/mo — about one-tenth the cost, with deeper personalization.",
      },
      {
        problem: "A serious teacher shortage",
        problemDetail: "With too few teachers, hands-on individual support is the first thing to go.",
        stat: "~64% of elementary and ~56% of middle schools reported shortages (2023)",
        solution: "An individual-tutoring assistant that lightens teachers' load and supports classrooms.",
      },
      {
        problem: "Learning that doesn't stick",
        problemDetail: "Alone, motivation fades at the first stumble or stretch of monotony.",
        stat: "Japan ranks 92nd of 116 in English — its lowest ever (EF EPI 2024)",
        solution: "A tutor that talks back and a garden you watch grow — built to keep you going to results.",
      },
    ],
    diagram: {
      title: "Why personal? The 2-sigma rule",
      caption: "Educational researcher Benjamin Bloom found that students tutored one-on-one outperformed about 98% of those in conventional classes — roughly a two–standard-deviation gain. Tane puts that “personal tutor” within everyone's reach.",
      conventional: "Conventional class",
      withTane: "Tutoring (Tane)",
      axis: "Distribution of achievement",
    },
  },
  usecases: {
    label: "Use cases",
    title: "For these people, in these moments.",
    lead: "Tane has no age or subject limits — it grows around what you want to learn.",
    imageAlt: "Use-case scene",
    items: [
      { tag: "Exam students", t: "Target weak spots, efficiently.", d: "Drill only what you're weak at. A plan built backwards from your target school makes the most of limited time." },
      { tag: "Working adults", t: "Reskill in spare minutes.", d: "English, programming, certifications — from a few minutes on the commute or before bed. Built to last." },
      { tag: "Parents", t: "A juku alternative, at home.", d: "No commute, no steep monthly fees. Individualized support tuned to your child's level, at home." },
      { tag: "Japanese learners", t: "Japanese for life and work.", d: "For foreign residents: practical Japanese matched to your level and purpose, one step at a time." },
      { tag: "Schools & juku", t: "A tutoring assistant for teachers.", d: "See class-wide and individual progress, so teachers can engage each student more deeply." },
      { tag: "Adult learners", t: "Back to basics, no shame.", d: "Re-learn middle and high school material at your own pace — privately, with no one watching." },
    ],
  },
  market: {
    label: "Why Nagoya, why now",
    title: "The market is moving.",
    lead: "AI and education are at an inflection point — a growing market, real problems, and Nagoya as the ideal base.",
    stats: [
      { value: "36.7%", label: "CAGR of Japan's AI-in-education market (to 2030, fastest in APAC)", source: "Grand View Research" },
      { value: "~¥370k", label: "Annual juku spend per child (public middle school)", source: "MEXT 2023" },
      { value: "92nd", label: "Japan's English-proficiency rank (of 116 — lowest ever)", source: "EF EPI 2024" },
      { value: "~64%", label: "Elementary schools reporting teacher shortages (2023)", source: "East Asia Forum" },
      { value: "+2σ", label: "Achievement gain from 1-on-1 tutoring", source: "Bloom's 2-sigma" },
      { value: "500+", label: "Startups at STATION Ai's launch (Japan's largest hub)", source: "JETRO 2024" },
    ],
    note: "Sources: Grand View Research / MEXT / EF EPI 2024 / East Asia Forum / JETRO and others. Market-size figures vary by definition and are shown as ranges.",
    chart: {
      title: "Japan's AI-in-education market (estimated)",
      caption: "Among the fastest-growing in Asia-Pacific — roughly 36.7% CAGR, expanding toward 2030.",
      cagr: "CAGR 36.7%",
      unit: "Market size (indexed, 2024 = 100)",
      years: ["2024", "2025", "2026", "2027", "2028", "2029", "2030"],
    },
    place: {
      title: "Based in Nagoya, Aichi",
      caption: "STATION Ai, opened Oct 2024, is Japan's largest startup hub with 500+ resident startups. Aichi has ~7.46M people, the Chubu region is ~11% of Japan's GDP, and foreign residents are at a record high — the intersection of education need and opportunity.",
      alt: "Nagoya / STATION Ai scene",
    },
  },
  pricing: {
    label: "Pricing",
    title: "A price within everyone's reach.",
    lead: "Start free. Upgrade to a monthly plan far lighter than juku whenever you need to. Change or cancel anytime.",
    billingNote: "Prices include tax. Annual plans save you two months.",
    perMonth: "/mo",
    tiers: [
      {
        name: "Seed", kana: "free", price: "¥0", unit: "", tagline: "Try it out",
        features: ["Adaptive learning, 1 subject", "AI tutor (up to 5/day)", "Learning garden (basic)", "No credit card required"],
        cta: "Start free", featured: false,
      },
      {
        name: "Sprout", kana: "popular", price: "¥1,980", unit: "/mo", tagline: "Most popular",
        features: ["All subjects, unlimited adaptive learning", "Unlimited AI tutor", "Full garden + spaced repetition", "Weak-spot reports", "~1/10 the cost of juku"],
        cta: "Get Sprout", featured: true,
      },
      {
        name: "Harvest", kana: "pro", price: "¥4,980", unit: "/mo", tagline: "For serious goals",
        features: ["Everything in Sprout", "Exam & certification tracks", "Priority AI (higher-accuracy model)", "Parent reports", "Monthly plan review"],
        cta: "Get Harvest", featured: false,
      },
      {
        name: "Grove", kana: "schools", price: "Custom", unit: "", tagline: "Schools, juku, teams",
        features: ["Teacher dashboard", "Class & assignment management", "Cohort & individual analytics", "SSO & admin", "Onboarding support"],
        cta: "Contact us", featured: false,
      },
    ],
  },
  faq: {
    label: "FAQ",
    title: "Frequently asked",
    items: [
      { q: "Which subjects are supported?", a: "English, math, Japanese, science, programming, and certifications like TOEIC — a broad range. Set a goal and Tane designs a plan around it." },
      { q: "Does the AI tutor really teach?", a: "Yes. Rather than handing over answers, it guides you to solve problems yourself through graduated hints and dialogue, using modern AI models." },
      { q: "How is this different from juku?", a: "It delivers the full personalization group classes can't — at about one-tenth the cost, with no constraints on time or place." },
      { q: "Is it safe for children?", a: "Learning data is handled with protection as the top priority, and parents can follow progress via reports. Age-appropriate design is in progress." },
      { q: "Can I use it for free?", a: "Yes. Start on the free Seed plan with no credit card, and upgrade whenever you need to." },
      { q: "Can schools and juku adopt it?", a: "Yes — the Grove plan includes a teacher dashboard and admin tools. Get in touch and we'll help you get set up." },
    ],
  },
  cta: {
    label: "Get started",
    title: "Plant your first seed.",
    lead: "Tane is preparing early access. Sign up and we'll let you know first when it opens. Schools, juku, collaborators and investors are warmly welcome too.",
    emailLabel: "Email",
    email: "mauryskull@gmail.com",
    button: "Join early access",
    placeholder: "Email address",
    note: "From Nagoya, Aichi. Personalized learning, powered by technology.",
  },
  footer: {
    tagline: "A seed of learning, for everyone.",
    status: "Early access — coming soon",
    location: "Nagoya, Aichi, Japan",
    rights: "© 2026 Tane. All rights reserved.",
  },
};

export const dict = { ja, en } as const;
